import {
  NinetailedAnalyticsPlugin,
  SanitizedElementSeenPayload,
  Template,
  TrackComponentProperties
} from '@ninetailed/experience.js-plugin-analytics'
import { template } from '@ninetailed/experience.js-shared'

type NinetailedHeapPluginOptions = {
  eventNameTemplate?: string
  categoryPropertyTemplate?: string
  componentPropertyTemplate?: string
  audiencePropertyTemplate?: string
  template?: Template
}

const TEMPLATE_OPTIONS = {
  interpolate: /{{([\s\S]+?)}}/g
}

const isHeapInitialized = () => {
  return (
    typeof window === 'object' &&
    'heap' in window &&
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    typeof window.heap !== 'undefined'
  )
}

export const transformCustomTrackingId = (customTrackingId: string) => {
  return customTrackingId.toLowerCase().replace(/\s+/g, '_')
}

/**
 * Sends the same Heap user/event props as {@link NinetailedHeapPlugin.onTrackExperience}
 * for a known variant index, but only when Heap + analytics consent are ready.
 * Returns whether anything was sent (false = caller should retry after Fides/Heap load).
 */
type HeapWindow = Window & {
  Fides?: { consent?: { analytics?: boolean } }
  heap?: {
    addEventProperties: (p: Record<string, string>) => void
    addUserProperties: (p: Record<string, string>) => void
    track: (name: string, p: Record<string, string>) => void
  }
}

function getHeapWindow(): HeapWindow | undefined {
  const hasHeap = (w: unknown): w is HeapWindow =>
    typeof w === 'object' &&
    w != null &&
    'heap' in w &&
    typeof (w as HeapWindow).heap !== 'undefined'

  if (typeof window === 'undefined') {
    return undefined
  }
  return hasHeap(window) ? (window as HeapWindow) : undefined
}

export const pushNinetailedFooterExperienceHeap = (
  experienceLabel: string | undefined,
  variantIndex: number
): boolean => {
  const win = getHeapWindow()
  if (win == null) {
    return false
  }

  const label =
    experienceLabel != null && String(experienceLabel).trim() !== ''
      ? String(experienceLabel)
      : ''
  if (!label) {
    return false
  }

  if (
    typeof win !== 'object' ||
    !('heap' in win) ||
    typeof win.heap === 'undefined'
  ) {
    return false
  }

  if (!win.Fides?.consent?.analytics) {
    return false
  }

  const trackingId = transformCustomTrackingId(label)
  const ninetailed_variant =
    variantIndex === 0 ? 'control' : `variant ${variantIndex}`

  win.heap.addUserProperties({ [trackingId]: ninetailed_variant })
  win.heap.addEventProperties({ [trackingId]: ninetailed_variant })
  win.heap.track(`Heap Analytics Initialized ${trackingId}`, {
    [trackingId]: ninetailed_variant
  })

  return true
}

/** Same as {@link pushNinetailedFooterExperienceHeap}; alias for non-footer call sites. */
export const pushNinetailedExperienceHeap = pushNinetailedFooterExperienceHeap

export default class NinetailedHeapPlugin extends NinetailedAnalyticsPlugin {
  public name = 'ninetailed:heap'

  constructor(private options: NinetailedHeapPluginOptions = {}) {
    super({
      ...options.template,
      customTrackingId: '{{experience.name}}',
      // eslint-disable-next-line camelcase
      ninetailed_variant: '{{selectedVariantSelector}}',
      // eslint-disable-next-line camelcase
      ninetailed_experience: '{{experience.id}}',
      // eslint-disable-next-line camelcase
      ninetailed_experience_name: '{{experience.name}}',
      // eslint-disable-next-line camelcase
      ninetailed_audience: '{{audience.id}}',
      // eslint-disable-next-line camelcase
      ninetailed_component: '{{selectedVariant.id}}'
    })
  }

  protected async onTrackExperience(
    properties: SanitizedElementSeenPayload,
    hasSeenExperienceEventPayload: Record<string, string>
  ): Promise<void> {
    if (typeof window === 'undefined') {
      return
    }

    if (!isHeapInitialized()) {
      return
    }

    if (!window.Fides?.consent?.analytics) {
      return
    }

    // eslint-disable-next-line camelcase
    const { customTrackingId, ninetailed_variant } =
      hasSeenExperienceEventPayload
    if (customTrackingId && customTrackingId !== 'undefined') {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const trackingId = transformCustomTrackingId(customTrackingId)
      // eslint-disable-next-line camelcase
      window.heap.addUserProperties({ [trackingId]: ninetailed_variant })
      // eslint-disable-next-line camelcase
      window.heap.addEventProperties({ [trackingId]: ninetailed_variant })
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      window.heap.track(`Heap Analytics Initialized ${trackingId}`, {
        [trackingId]: ninetailed_variant
      })
    }
  }

  protected async onTrackComponent(
    properties: TrackComponentProperties
  ): Promise<void> {
    if (typeof window === 'undefined') {
      return
    }

    if (!isHeapInitialized()) {
      return
    }

    if (!window.Fides?.consent?.analytics) {
      return
    }

    const { variant, audience, isPersonalized } = properties
    const event = template(
      this.options.eventNameTemplate ||
        'Has Seen Component - Audience:{{ audience.id }}',
      { variant, audience, isPersonalized },
      TEMPLATE_OPTIONS.interpolate
    )
    const categoryProperty = template(
      this.options.categoryPropertyTemplate || 'Ninetailed',
      {
        variant,
        component: variant,
        audience,
        isPersonalized
      },
      TEMPLATE_OPTIONS.interpolate
    )
    const componentProperty = template(
      this.options.componentPropertyTemplate || '{{ component.id }}',
      {
        variant,
        component: variant,
        audience,
        isPersonalized
      },
      TEMPLATE_OPTIONS.interpolate
    )
    const audienceProperty = template(
      this.options.audiencePropertyTemplate || '{{ audience.id }}',
      {
        variant,
        component: variant,
        audience,
        isPersonalized
      },
      TEMPLATE_OPTIONS.interpolate
    )

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    window.heap.track(event, {
      category: categoryProperty,
      component: componentProperty,
      audience: audienceProperty,
      isPersonalized
    })
  }
}
